/**
 * Componente de botón de información
 * Muestra tutorial de instrucciones en español
 */

import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useScreenOrientation } from '../../hooks/useScreenOrientation';
import { LottieAvatar } from '../Avatar/LottieAvatar';
import './InfoButton.css';

interface InfoButtonProps {
  onToggle?: (isVisible: boolean) => void;
  /** Modo profesor (aide-prof): tutorial en francés. Por defecto español. */
  aideProf?: boolean;
}

export function InfoButton({ onToggle, aideProf = false }: InfoButtonProps) {
  // Textos del tutorial. En modo profesor toda la UI va en francés.
  const t = aideProf
    ? {
        show: 'Afficher les informations',
        close: 'Fermer',
        mic: 'Appuie pour activer le micro et lancer l’enregistrement.',
        sendAudio: 'Appuie pour envoyer l’audio.',
        toggleChat: 'Appuie pour afficher ou masquer le chat.',
        replay: 'Appuie sur la bulle d’un message de Galia pour réécouter l’audio.',
        translate: 'Appuie sur l’icône de traduction d’un message de Galia pour le voir en espagnol.',
        sendMsg: 'Appuie pour envoyer ton message.',
        tts: 'Appuie sur le haut-parleur pour activer ou couper la voix de Galia.',
        stop: 'Appuie sur « Arrêter » pour interrompre la réponse de Galia.',
        settings: 'Appuie sur l’engrenage pour modifier la configuration.',
        contents: 'Appuie sur le livre pour explorer les contenus du cours.',
        trophies: 'Appuie sur la coupe pour voir tes trophées et ta progression.',
      }
    : {
        show: 'Mostrar información',
        close: 'Cerrar',
        mic: 'Presiona para activar el micrófono e iniciar la grabación.',
        sendAudio: 'Presiona para enviar el audio.',
        toggleChat: 'Presiona para mostrar u ocultar el chat.',
        replay: 'Presiona la burbuja de un mensaje de Galia para repetir el audio.',
        translate: 'Presiona el ícono de traducción en un mensaje de Galia para verlo en español.',
        sendMsg: 'Presiona para enviar tu mensaje.',
        tts: 'Presiona el altavoz para activar o silenciar la voz de Galia.',
        stop: 'Presiona «Detener» para interrumpir la respuesta de Galia.',
        settings: 'Presiona el engranaje para cambiar la configuración (personalidad, nivel, acento…).',
        contents: 'Presiona el libro para explorar los contenidos del curso.',
        trophies: 'Presiona la copa para ver tus trofeos y tu progreso.',
      };

  // Iconos nuevos de la barra de controles (altavoz, Detener, engranaje,
  // libro, copa). Un solo arreglo compartido por ambos layouts; el trofeo
  // solo existe para estudiantes (en modo profesor el botón no se muestra).
  const extraItems: Array<{ key: string; cls: string; text: string; icon: JSX.Element }> = [
    {
      key: 'tts',
      cls: 'instruction-icon--tts',
      text: t.tts,
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
      ),
    },
    {
      key: 'stop',
      cls: 'instruction-icon--stop',
      text: t.stop,
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white" stroke="none">
          <rect x="7" y="7" width="10" height="10" rx="2" />
        </svg>
      ),
    },
    {
      key: 'settings',
      cls: 'instruction-icon--settings',
      text: t.settings,
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
    },
    {
      key: 'contents',
      cls: 'instruction-icon--contents',
      text: t.contents,
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          <polygon points="11 7 15 9.5 11 12" fill="white" stroke="none" />
        </svg>
      ),
    },
    ...(!aideProf
      ? [
          {
            key: 'trophies',
            cls: 'instruction-icon--trophy',
            text: t.trophies,
            icon: (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 21h8" />
                <path d="M12 17v4" />
                <path d="M7 4h10v6a5 5 0 0 1-10 0V4z" />
                <path d="M7 6H4a2 2 0 0 0 2 5h1" />
                <path d="M17 6h3a2 2 0 0 1-2 5h-1" />
              </svg>
            ),
          },
        ]
      : []),
  ];
  const [isInformationVisible, setIsInformationVisible] = useState(false);
  const orientation = useScreenOrientation();
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const instructionsRef = useRef<HTMLDivElement>(null);

  const toggleInformation = () => {
    const newState = !isInformationVisible;
    setIsInformationVisible(newState);
    onToggle?.(newState);
  };

  return (
    <>
      <button
        className={`info-button ${isInformationVisible ? 'hidden' : ''}`}
        onClick={toggleInformation}
        aria-label={t.show}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="info-icon">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
      </button>

      {isInformationVisible && createPortal(
        <div className={`information-menu ${orientation}`}>
          {orientation === 'landscape' && (
            <div 
              className="menu-background"
              onClick={toggleInformation}
            />
          )}
          
          {orientation === 'landscape' ? (
            <div className="tutorial-panel-horizontal">
              <button
                className="close-button"
                onClick={toggleInformation}
                aria-label={t.close}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>

              <div className="tutorial-instructions">
                <div className="instruction-item">
                  <div className="instruction-icon instruction-icon--mic">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                  </div>
                  <p className="instruction-text">{t.mic}</p>
                </div>

                <div className="instruction-item">
                  <div className="instruction-icon instruction-icon--audio">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="6" y="6" width="12" height="12" rx="2"/>
                    </svg>
                  </div>
                  <p className="instruction-text">{t.sendAudio}</p>
                </div>

                <div className="instruction-item">
                  <div className="instruction-icon instruction-icon--chat">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                    </svg>
                  </div>
                  <p className="instruction-text">{t.toggleChat}</p>
                </div>

                <div className="instruction-item">
                  <div className="instruction-icon instruction-icon--audio-replay">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(26,58,55,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                    </svg>
                  </div>
                  <p className="instruction-text">{t.replay}</p>
                </div>

                <div className="instruction-item">
                  <div className="instruction-icon instruction-icon--translate">
                    <svg width="28" height="28" viewBox="0 0 20 20" role="img" aria-hidden="true" style={{ clipPath: 'circle(50%)', display: 'block' }}><rect x="0" y="0" width="20" height="10" fill="#FCD116" /><rect x="0" y="10" width="20" height="5" fill="#003893" /><rect x="0" y="15" width="20" height="5" fill="#CE1126" /></svg>
                  </div>
                  <p className="instruction-text">{t.translate}</p>
                </div>

                <div className="instruction-item">
                  <div className="instruction-icon instruction-icon--send">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </div>
                  <p className="instruction-text">{t.sendMsg}</p>
                </div>

                {extraItems.map((it) => (
                  <div className="instruction-item" key={it.key}>
                    <div className={`instruction-icon ${it.cls}`}>{it.icon}</div>
                    <p className="instruction-text">{it.text}</p>
                  </div>
                ))}

              </div>
            </div>
          ) : (
            <div className="tutorial-panel-vertical" ref={panelRef}>
              <div className="tutorial-header-vertical" ref={headerRef}>
                <img 
                  src="/assets/ui/otros/galia.png" 
                  alt="Galia" 
                  className="tutorial-logo-top-left"
                />
                <img 
                  src="/assets/ui/otros/logo_af.png" 
                  alt="Alianza Francesa" 
                  className="tutorial-logo-top-right"
                />
                <button
                  className="close-button-vertical-header"
                  onClick={toggleInformation}
                  aria-label={t.close}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary, #1a3a37)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
                <div className="tutorial-avatar-container">
                  <LottieAvatar mode="conversacional" preview={false} avatarId="avatar1" />
                </div>
              </div>
              
              <div className="tutorial-content-vertical" ref={contentRef}>
                <div className="tutorial-instructions-vertical" ref={instructionsRef}>
                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical instruction-icon--mic">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                      </svg>
                    </div>
                    <p className="instruction-text-vertical">{t.mic}</p>
                  </div>

                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical instruction-icon--audio">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="6" width="12" height="12" rx="2"/>
                      </svg>
                    </div>
                    <p className="instruction-text-vertical">{t.sendAudio}</p>
                  </div>

                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical instruction-icon--chat">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(26,58,55,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                      </svg>
                    </div>
                    <p className="instruction-text-vertical">{t.toggleChat}</p>
                  </div>

                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical instruction-icon--audio-replay">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(26,58,55,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                      </svg>
                    </div>
                    <p className="instruction-text-vertical">{t.replay}</p>
                  </div>

                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical instruction-icon--translate">
                      <svg width="28" height="28" viewBox="0 0 20 20" role="img" aria-hidden="true" style={{ clipPath: 'circle(50%)', display: 'block' }}><rect x="0" y="0" width="20" height="10" fill="#FCD116" /><rect x="0" y="10" width="20" height="5" fill="#003893" /><rect x="0" y="15" width="20" height="5" fill="#CE1126" /></svg>
                    </div>
                    <p className="instruction-text-vertical">{t.translate}</p>
                  </div>

                  <div className="instruction-item-vertical">
                    <div className="instruction-icon-vertical instruction-icon--send">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(26,58,55,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    </div>
                    <p className="instruction-text-vertical">{t.sendMsg}</p>
                  </div>

                  {extraItems.map((it) => (
                    <div className="instruction-item-vertical" key={it.key}>
                      <div className={`instruction-icon-vertical ${it.cls}`}>{it.icon}</div>
                      <p className="instruction-text-vertical">{it.text}</p>
                    </div>
                  ))}

                </div>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}


